import {EyeIcon, EyeSlashIcon, Flex, FormInput, Text} from "@fluentui/react-northstar";
import React, {memo, useContext, useEffect, useState} from "react";
import languageObject from "../../translations";
import {AppContext, IAppContextState} from "../../services/AppContext";

/**
 * Edit input props
 */
interface EditInputProps {
    label: string,
    edit?: boolean,
    value: string,
    setValue?: (value: string) => void,
    type?: "text" | "password",
    showSuccessIcon?: boolean,
    errorMessage?: string,
    required?: boolean,
    onVisibilityChange?: (value: boolean) => void,
    disabled?: boolean,
    resetFirstFocus?: boolean,
    inverted?: boolean,
    min?: number,
    max?: number,
    alreadyFocused?: boolean,
    styles?: object
    autoFocus?: boolean
}

/**
 * Edit input default props
 */
let defaultProps = {
    edit: true,
    type: "text",
    showSuccessIcon: false,
    errorMessage: "",
    required: false,
    disabled: false,
    resetFirstFocus: false,
    inverted: false,
    min: 0,
    max: 500,
    alreadyFocused: false,
    styles: {},
}

/**
 * Edit input
 * @param props properties
 */
function EditInput(props: EditInputProps) {
    // Context
    const context = useContext<IAppContextState>(AppContext);

    // Check if theme is light
    const isLightTheme = context.teams.isLightTheme;

    // Show input max length
    const showLength = props.max !== undefined;

    // Add default values to props
    props = {...defaultProps, ...props} as EditInputProps;

    /**
     * Input has been focused for one time ?
     */
    const [firstFocus, setFirstFocus] = useState(props.alreadyFocused || props.errorMessage !== "");

    /**
     * Show or hide password
     */
    const [showPassword, setShowPassword] = useState(false);

    /**
     * When component has updated
     */
    useEffect(() => {
        // Callback on password visibility
        if (props.onVisibilityChange)
            props.onVisibilityChange(showPassword);
        // Reset first focus
        if (props.resetFirstFocus)
            setFirstFocus(false);
    }, [showPassword, props.resetFirstFocus]);

    // If field is required, error will appear only if value is empty
    if (props.edit && props.required) {
        if (props.errorMessage === "") props.errorMessage = props.value.length === 0 ? languageObject.translate("FieldRequired") : "";
        if (!props.showSuccessIcon && !showLength) props.showSuccessIcon = props.value.length > 0;
    }

    return (
        <Flex fill styles={{marginBottom: "0 !important", height: "auto !important"}}>
            <FormInput
                fluid
                icon={(showLength && props.edit) && (
                    <Text styles={{color: "grey"}} content={props.value.length + "/" + props.max}/>
                )}
                inverted={props.inverted}
                className={"form-input"}
                styles={{...props.styles, width: "100%"}}
                type={props.type === "text" || (props.type === "password" && showPassword) ? "text" : "password"}
                minLength={props.min}
                maxLength={props.max}
                autoFocus={props.autoFocus}
                label={
                    <>
                        <Text>
                            {props.label}
                            {props.required && <Text content={" *"} color={"red"}/>}
                        </Text>
                        {props.type === "password" &&
                            (showPassword ?
                                <EyeIcon styles={{color: isLightTheme ? "black" : "white", cursor: "pointer"}}
                                         xSpacing="before" onClick={() => setShowPassword(false)}/>
                                :
                                <EyeSlashIcon styles={{color: isLightTheme ? "black" : "white", cursor: "pointer"}}
                                              xSpacing="before" onClick={() => setShowPassword(true)}/>)
                        }
                    </>
                }
                input={{styles: {paddingRight: (!props.edit || props.disabled) ? "12px" : showLength ? "70px" : "35px"}}}
                readOnly={!props.edit || props.disabled}
                autoComplete={props.label}
                showSuccessIndicator={!showLength && props.edit && props.showSuccessIcon && props.errorMessage === ""}
                errorMessage={props.edit && firstFocus && props.errorMessage}
                value={props.value}
                onChange={(e, input) => props.edit && props.setValue &&
                    props.setValue(input?.value ?? "")}
                onFocus={() => setFirstFocus(true)}/>
        </Flex>
    );
}

export default memo(EditInput);